import React, { useState, useRef, useEffect, FC } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import FlagIcon from '@mui/icons-material/Flag';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import LockIcon from '@mui/icons-material/Lock';
import format from 'date-fns/format';

import { Event } from 'modules/events/types/eventsTypes';
import { eventsCategories } from 'modules/events/constants/eventsCategories';
import { sanitizeHtml, scrollToRef } from 'shared/functions';
import { Loader } from 'ui';
import {
  addEventToFavourites,
  removeEventFromFavourites,
} from 'modules/events/services/eventsService';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { getSelectedItems } from 'shared/functions/getSelectedItems';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { usePortfolioModals, useFeatures } from 'shared/hooks';
import { fetchEventsAction } from 'modules/events/store/eventsActions';
import { EventsProductDetails } from '../EventsProductDetails/EventsProductDetails';
import { EventsActionButton } from '../EventsActionButton';

const useStyles = makeStyles(theme => ({
  name: {
    fontSize: 28,
    backgroundColor: grey[600],
    color: theme.palette.primary.contrastText,
    borderRadius: '4px 4px 0 0',
  },
  container: {
    height: 400,
  },
  label: {
    color: theme.palette.primary.main,
    fontSize: 14,
  },
  value: {
    fontWeight: 500,
    fontSize: 18,
  },
  labelBar: {
    backgroundColor: grey[400],
    height: '100%',
    borderRadius: '4px 0 0 4px',
    color: theme.palette.primary.contrastText,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 24,
  },
  image: {
    height: 330,
  },
  thirdPartyImage: {
    maxWidth: '50%',
    maxHeight: '50%',
  },
  description: {
    borderLeft: `1px solid ${grey[300]}`,
    borderRight: `1px solid ${grey[300]}`,
    '& > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 9,
      '-webkit-box-orient': 'vertical',
      fontSize: 18,
      fontWeight: 500,
      textAlign: 'justify',
      margin: theme.spacing(1.5, 4, 4),
    },
  },
  product: {
    marginTop: 15,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  actionContainer: {
    height: '100%',
  },
  priceBox: {
    width: 160,
    borderStyle: 'dashed',
    borderColor: grey[600],
    borderWidth: '0 0 1px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexDirection: 'column',
  },
  price: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  pricePeriod: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  buttonContainer: {
    textAlign: 'center',
  },
  buttonActive: {
    backgroundColor: '#ff8900',
  },
  actionButton: {
    height: 45,
    marginTop: 10,
  },
  favoriteButton: {
    width: 45,
    height: 45,
    padding: 0,
    minWidth: 'auto',
    marginLeft: 6,
    marginTop: 10,
  },
  trialText: {
    display: 'block',
    maxWidth: 160,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 0,
  },
  freeText: {
    fontSize: 22,
  },
  hiddenCheckoutButton: {
    visibility: 'hidden',
  },
  loader: {
    display: 'inline-block',
    position: 'relative',
    margin: '0 10px',
    top: 18,
  },
  lockIcon: {
    fontSize: 100,
    color: theme.palette.primary.main,
  },
  esgAnalysisHeader: { backgroundColor: theme.palette.custom.esgAnalysisEvents },
  fundAnalysisHeader: { backgroundColor: theme.palette.custom.fundAnalysisEvents },
  creditAnalysisHeader: { backgroundColor: theme.palette.custom.creditAnalysisEvents },
  bold: {
    fontWeight: 'bold',
  },
  date: {
    fontSize: 24,
  },
}));

type EventsProductProps = {
  data: Event;
  preview?: boolean;
  collapsed?: boolean;
};

const EventsProduct: FC<EventsProductProps> = ({ data, preview, collapsed }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const productRef = useRef(null);
  const [isFavoriteStatusLoading, setIsFavoriteStatusLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(collapsed);
  const { addToPortfolio, createPortfolio } = usePortfolioModals();
  const { eventsInPortfolioManager } = useFeatures();

  const { productId: scrollToProductId } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const isFavorite = data.favourite;

  const scrollToProduct = () => {
    scrollToRef(productRef);
  };

  const changeFavoriteStatus = async () => {
    setIsFavoriteStatusLoading(true);

    await (isFavorite
      ? removeEventFromFavourites(data.id as string)
      : addEventToFavourites(data.id as string));

    dispatch(fetchEventsAction());

    setIsFavoriteStatusLoading(false);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (showDetails) {
      timer = setTimeout(() => scrollToProduct(), 300);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showDetails]);

  useEffect(() => {
    // @ts-ignore
    if (scrollToProductId && scrollToProductId === data.id && productRef.current) {
      setShowDetails(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRef.current]);

  const date = new Date(data.eventDate);
  const locked = false;
  const image =
    typeof data.image === 'string'
      ? `data:image/png;base64, ${data.image}`
      : URL.createObjectURL(data.image);

  /*
    Since `ref` is not available in Box props typescript definition (https://github.com/mui-org/material-ui/issues/17010)
    we spread it, to prevent from typescript error (commenting with `@ts-ignore` fails on minification). 
  */
  const rootProps = {
    ref: productRef,
  };

  return (
    <Box mb={6} {...rootProps}>
      <Paper elevation={3}>
        <Box
          p={2}
          className={clsx({
            [classes.name]: true,
            [classes.esgAnalysisHeader]: data.category === 'ESG_ANALYSIS',
            [classes.fundAnalysisHeader]: data.category === 'FUNDS_ANALYSIS',
            [classes.creditAnalysisHeader]: data.category === 'CREDIT_ANALYSIS',
          })}
        >
          <Typography variant="h5">{data.title}</Typography>
        </Box>
        <Grid container>
          <img alt={t('Logo')} src={image} className={classes.image} />
          <Grid container item xs direction="column" justifyContent="space-between">
            <Box px={4} pt={4}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography className={classes.label}>{t('Category')}</Typography>
                  <Typography className={classes.value}>
                    {eventsCategories[data.category]}
                  </Typography>
                </Grid>
                <Grid item xs={6} container justifyContent="flex-end">
                  <Box>
                    <Typography className={classes.label}>{t('Region')}</Typography>
                    <Typography className={classes.value}>{data.region}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.label}>{t('Industry')}</Typography>
                  <Typography className={classes.value}>{data.industry}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box px={4} pb={4}>
              <Typography className={classes.bold} variant="h6" align="justify">
                {data.marketingLabel}
              </Typography>
            </Box>
          </Grid>
          {locked && (
            <Grid
              item
              xs
              container
              direction="column"
              alignItems="center"
              justifyContent="space-evenly"
              className={classes.description}
            >
              <LockIcon className={classes.lockIcon} />
              <Typography color="primary" variant="h6" align="center">
                <strong>
                  Get started to gain access
                  <br />
                  to this event
                </strong>
              </Typography>
            </Grid>
          )}
          {!locked && (
            <Grid item xs className={classes.description}>
              {sanitizeHtml(data.shortDescription)}
            </Grid>
          )}
          <Box px={4} py={4}>
            <Grid container direction="column">
              <Paper>
                <Box>
                  <Typography align="center">
                    <strong>
                      <small>{format(date, 'EEEE').toUpperCase()}</small>
                    </strong>
                  </Typography>
                  <Typography align="center" variant="h6" className={classes.date}>
                    <strong>{format(date, 'd MMM yyyy').toUpperCase()}</strong>
                  </Typography>
                  <Typography align="center" color="primary">
                    <strong>
                      <small>{format(date, 'K.mm bbbb')}</small>
                    </strong>
                  </Typography>
                </Box>
              </Paper>
              <EventsActionButton
                event={data}
                className={clsx(classes.buttonActive, classes.actionButton)}
                disabled={preview}
              />
              {eventsInPortfolioManager && (
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.actionButton}
                  onClick={addToPortfolio.toggle}
                  disabled={preview}
                >
                  {t('Add to portfolio')}
                </Button>
              )}
              <Grid container>
                <Button
                  variant={showDetails ? 'contained' : 'outlined'}
                  color="primary"
                  className={classes.actionButton}
                  onClick={toggleDetails}
                  disabled={preview}
                >
                  {t('More info')}
                </Button>
                {isFavoriteStatusLoading ? (
                  <Loader size={30} classes={classes.loader} />
                ) : (
                  <Button
                    variant={isFavorite ? 'contained' : 'outlined'}
                    color="primary"
                    className={classes.favoriteButton}
                    onClick={changeFavoriteStatus}
                    disabled={preview}
                  >
                    <FlagIcon />
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Paper>
      <EventsProductDetails product={data} expanded={!!showDetails} preview={preview} />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        itemsData={getSelectedItems(data.id || '')}
        onClickCreateNew={createPortfolio.show}
        variant="add"
        itemType="EVENT"
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={getSelectedItems(data.id || '')}
        itemType="EVENT"
      />
    </Box>
  );
};

export { EventsProduct };
